import { useEffect, useState } from "react";
import { Plus, Search } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useCan } from "@/lib/auth";
import { cn } from "@/lib/utils";

import {
  BASIS_LABEL,
  ROLE_LABEL,
  fmtMoney2,
  fmtRate,
  monthLabel,
  useContractLedger,
  useContractSearch,
  type Assignment,
  type LedgerActual,
  type LedgerPayee,
} from "../api";
import { AssignmentDialog } from "./AssignmentDialog";

/**
 * The per-contract accounting ledger: the forecast schedule (P1 + M1..Mn per
 * payee) overlaid with actual payout lines from the monthly runs. Paste a
 * contract id (from the Contracts section) to load its ledger.
 */
export function LedgerView() {
  const [input, setInput] = useState("");
  const [query, setQuery] = useState("");
  const [contractId, setContractId] = useState("");
  const isAdmin = useCan("commissions.admin");
  const { data, error, isLoading, refetch } = useContractLedger(contractId);
  const search = useContractSearch(query);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<Assignment | null>(null);

  // Debounce typing → warehouse search (min 3 chars).
  useEffect(() => {
    const handle = setTimeout(() => setQuery(input), 400);
    return () => clearTimeout(handle);
  }, [input]);

  const pick = (id: string) => {
    setContractId(id);
    setInput("");
    setQuery("");
  };
  const showResults = query.trim().length >= 3 && input === query;

  return (
    <div className="flex flex-col gap-4">
      <form
        className="flex items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          // Fallback path: pasting an exact contract ID still works.
          if (input.trim()) pick(input.trim());
        }}
      >
        <div className="relative w-full max-w-md">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-8"
            placeholder="Search by company name (or paste a contract ID)"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            aria-label="Contract ID"
          />
        </div>
        <Button type="submit" variant="outline" disabled={!input.trim()}>
          Load ledger
        </Button>
      </form>

      {showResults && (
        <Card className="max-w-2xl overflow-hidden p-0">
          {search.isLoading && (
            <p className="px-4 py-3 text-sm text-muted-foreground">Searching contracts…</p>
          )}
          {search.data && search.data.length === 0 && (
            <p className="px-4 py-3 text-sm text-muted-foreground">
              No contracts match "{query}".
            </p>
          )}
          {(search.data ?? []).map((hit) => (
            <button
              key={hit.contract_id}
              type="button"
              className="flex w-full items-center justify-between gap-3 border-b border-border px-4 py-2.5 text-left transition-colors last:border-0 hover:bg-muted/50"
              onClick={() => pick(hit.contract_id)}
            >
              <span className="min-w-0">
                <span className="block truncate font-medium">
                  {hit.company_name ?? "—"}
                  {hit.company_name_dba ? (
                    <span className="text-muted-foreground"> dba {hit.company_name_dba}</span>
                  ) : null}
                </span>
                <span className="block text-xs text-muted-foreground">
                  {hit.contract_type_name ?? "—"}
                  {hit.contract_signed_at ? ` · signed ${hit.contract_signed_at}` : ""}
                  {hit.closer_name ? ` · closer ${hit.closer_name}` : ""}
                </span>
              </span>
              <span className="whitespace-nowrap text-sm tabular-nums text-muted-foreground">
                {hit.mrr_total != null ? `${fmtMoney2(hit.mrr_total)}/mo` : ""}
              </span>
            </button>
          ))}
        </Card>
      )}

      {!contractId && !showResults && (
        <Card className="p-8 text-center text-sm text-muted-foreground">
          Search a company name to find its contract — the ledger shows who's
          paid, the forecast by month, and every actual payout to date.
        </Card>
      )}

      {error != null && contractId && (
        <Alert variant="danger">
          <AlertTitle>Couldn't load the ledger</AlertTitle>
          <AlertDescription>
            {error instanceof Error ? error.message : String(error)}
          </AlertDescription>
        </Alert>
      )}

      {isLoading && contractId && <Card className="h-40 animate-pulse" />}

      {data && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h2 className="text-lg font-semibold">
                {data.company_name ?? "Contract"}{" "}
                <span className="text-sm font-normal text-muted-foreground">
                  · {data.contract_id}
                </span>
              </h2>
            </div>
            {isAdmin && (
              <Button
                size="sm"
                onClick={() => {
                  setEditing(null);
                  setEditorOpen(true);
                }}
              >
                <Plus className="mr-1.5 h-4 w-4" />
                Add payee
              </Button>
            )}
          </div>

          {data.payees.length === 0 && (
            <Card className="p-8 text-center text-sm text-muted-foreground">
              {isAdmin
                ? "No commission assignments on this contract yet — add a payee to start the ledger."
                : "No commission assignments visible to you on this contract."}
            </Card>
          )}

          {data.payees.map((p) => (
            <PayeeLedgerCard key={p.payee_id} payee={p} />
          ))}

          {data.assignments.length > 0 && (
            <AssignmentsTable
              assignments={data.assignments}
              isAdmin={isAdmin}
              onEdit={(a) => {
                setEditing(a);
                setEditorOpen(true);
              }}
            />
          )}

          <AssignmentDialog
            open={editorOpen}
            onOpenChange={(o) => {
              setEditorOpen(o);
              if (!o) void refetch();
            }}
            contractId={data.contract_id}
            existing={editing}
            siblings={data.assignments}
          />
        </>
      )}
    </div>
  );
}

function PayeeLedgerCard({ payee }: { payee: LedgerPayee }) {
  // Index actuals by the month they cover for overlay lookups.
  const actualsByIndex = new Map<string, LedgerActual[]>();
  for (const a of payee.actuals) {
    const key = a.month_index ?? a.period;
    const list = actualsByIndex.get(key) ?? [];
    list.push(a);
    actualsByIndex.set(key, list);
  }

  return (
    <Card className="overflow-hidden p-0">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-muted/40 px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="font-semibold">{payee.payee_name}</span>
          {payee.role && (
            <Badge variant="secondary">{ROLE_LABEL[payee.role as never] ?? payee.role}</Badge>
          )}
        </div>
        <div className="flex items-center gap-4 text-sm tabular-nums">
          <span>
            Forecast <span className="font-semibold">{fmtMoney2(payee.forecast_total)}</span>
          </span>
          <span>
            Paid to date{" "}
            <span className="font-semibold text-success">{fmtMoney2(payee.actual_total)}</span>
          </span>
        </div>
      </div>

      {payee.forecast.length > 0 ? (
        <div className="overflow-x-auto px-4 py-3">
          <TooltipProvider delayDuration={200}>
          <div className="flex min-w-max items-end gap-1.5">
            {payee.forecast.map((cell) => {
              const actuals = actualsByIndex.get(cell.month_index) ?? [];
              const paid = actuals.filter(
                (a) => a.status === "payable" || a.status === "catchup" || a.status === "imported",
              );
              const held = actuals.some((a) => a.status === "held_unpaid_invoice");
              const paidTotal = paid.reduce((s, a) => s + a.amount, 0);
              const anyLocked = actuals.some((a) => a.locked);
              return (
                <Tooltip key={cell.month_index}>
                  <TooltipTrigger asChild>
                    <div
                      className={cn(
                        "flex w-[72px] flex-col items-center gap-1 rounded-md border px-1.5 py-2 text-center",
                        paid.length > 0 && "border-success/50 bg-success-light",
                        held && paid.length === 0 && "border-warning/50 bg-warning-light",
                        actuals.length === 0 && "border-dashed border-border",
                        anyLocked && "ring-1 ring-border",
                      )}
                    >
                      <span
                        className={cn(
                          "text-[10px] font-semibold uppercase tracking-wider",
                          cell.month_index === "P1"
                            ? "text-power"
                            : "text-muted-foreground",
                        )}
                      >
                        {cell.month_index}
                      </span>
                      <span className="text-xs tabular-nums text-muted-foreground">
                        {fmtMoney2(cell.amount)}
                      </span>
                      {paid.length > 0 && (
                        <span className="text-xs font-semibold tabular-nums text-success">
                          {fmtMoney2(paidTotal)}
                        </span>
                      )}
                      {held && paid.length === 0 && (
                        <span className="text-[10px] font-medium text-warning">held</span>
                      )}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="text-xs">
                      <p className="font-semibold">
                        {cell.month_index}
                        {cell.period ? ` · ${monthLabel(cell.period)}` : ""}
                      </p>
                      <p>Forecast {fmtMoney2(cell.amount)}</p>
                      {actuals.map((a, i) => (
                        <p key={i}>
                          {a.status === "held_unpaid_invoice"
                            ? "Held"
                            : a.status === "catchup"
                              ? `Paid ${monthLabel(a.period)} (catch-up)`
                              : `Paid ${monthLabel(a.period)}`}
                          {" — "}
                          {fmtMoney2(a.amount)}
                          {a.invoice_document_id ? ` · inv ${a.invoice_document_id}` : ""}
                          {a.locked ? " · locked" : ""}
                        </p>
                      ))}
                    </div>
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </div>
          </TooltipProvider>
        </div>
      ) : (
        <p className="px-4 py-3 text-sm text-muted-foreground">
          No forecast (window start unknown) — actuals below.
        </p>
      )}

      {payee.actuals.length > 0 && (
        <div className="border-t border-border px-4 py-2 text-xs text-muted-foreground">
          {payee.actuals.length} payout line{payee.actuals.length === 1 ? "" : "s"} across{" "}
          {new Set(payee.actuals.map((a) => a.period)).size} month(s); locked months are
          outlined.
        </div>
      )}
    </Card>
  );
}

function AssignmentsTable({
  assignments,
  isAdmin,
  onEdit,
}: {
  assignments: Assignment[];
  isAdmin: boolean;
  onEdit: (a: Assignment) => void;
}) {
  return (
    <Card className="overflow-hidden p-0">
      <div className="border-b border-border bg-muted/40 px-4 py-2.5 text-sm font-semibold">
        Assignments
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Payee</TableHead>
              <TableHead>Role</TableHead>
              <TableHead className="text-right">Rate</TableHead>
              <TableHead>Basis</TableHead>
              <TableHead>Months</TableHead>
              <TableHead>P1</TableHead>
              <TableHead>Scope</TableHead>
              <TableHead>Status</TableHead>
              {isAdmin && <TableHead />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {assignments.map((a) => (
              <TableRow key={a.id}>
                <TableCell className="font-medium">{a.payee_name}</TableCell>
                <TableCell>{ROLE_LABEL[a.role]}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {a.fixed_monthly_amount != null
                    ? `${fmtMoney2(a.fixed_monthly_amount)}/mo`
                    : fmtRate(a.rate_pct)}
                  {a.off_plan && a.plan_rate_pct != null && (
                    <span className="ml-1 text-xs text-warning">
                      (plan {fmtRate(a.plan_rate_pct)})
                    </span>
                  )}
                </TableCell>
                <TableCell>{BASIS_LABEL[a.basis]}</TableCell>
                <TableCell className="tabular-nums">
                  M{a.month_from}–M{a.month_to}
                </TableCell>
                <TableCell>
                  {a.includes_p1 ? <Badge variant="info">P1</Badge> : "—"}
                </TableCell>
                <TableCell className="max-w-[160px] truncate">
                  {a.item_scope?.length ? a.item_scope.join(", ") : "All items"}
                </TableCell>
                <TableCell>
                  {a.status === "pending_approval" ? (
                    <Badge variant="warning">Pending approval</Badge>
                  ) : a.status === "ended" ? (
                    <Badge variant="neutral">Ended</Badge>
                  ) : (
                    <Badge variant="success">Active</Badge>
                  )}
                </TableCell>
                {isAdmin && (
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => onEdit(a)}>
                      Edit
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}
